import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import {
  AccordionDetails,
  AccordionSummary,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, memo, useCallback } from "react";
import { Form } from "react-final-form";
import { Accordion } from "../../../../components/Accordion";
import { mainTheme } from "../../../../theme/mainTheme";
import { TDocument } from "../../models";
import { PayloadSaveConfirmation, SaveBar } from "./SaveBar";

export type AccordionShellProps = {
  title: string;
  document?: TDocument;
  companyRole: string;
  comments: Record<string, string>;
  addComment: (userDocumentId: string, comment: string) => void;
  /**
   * Initial values for the document's RFF main payload form. Defaults to
   * an empty object. Renderers seed this from the loaded main binary entry.
   * When the parent passes a new object reference (after a successful save
   * + document refetch) RFF reinitializes the form and dirty state clears.
   */
  initialValues?: Record<string, unknown>;
  /**
   * When true, the Save button can persist a dirty main payload form via
   * `onSavePayload`. When false, only the compliance comment is savable.
   */
  canEditPayload?: boolean;
  /**
   * Marks a synthetic primary row that has not been persisted yet. This keeps
   * Save available for a valid, pristine compatibility-repair payload even
   * when the containing document itself already has an id.
   */
  requiresPayloadSave?: boolean;
  /**
   * Persist the current main payload. Called when the form is dirty, or when
   * an editable empty-id document requires its first save. Pending-document
   * implementations return the newly persisted document id so SaveBar can
   * attach a comment to it.
   */
  onSavePayload?: (
    values: Record<string, unknown>,
  ) => Promise<string | void>;
  /**
   * Optional confirmation shown before a dirty, valid payload save. Comment-only
   * saves bypass this hook.
   */
  payloadSaveConfirmation?: PayloadSaveConfirmation;
  /**
   * Run after a successful Save (after both payload and comment have been
   * persisted). Renderers typically refetch documents here so the new
   * authoritative payload becomes initialValues.
   */
  onSaveSuccess?: () => Promise<void> | void;
  defaultExpanded?: boolean;
  children?: ReactNode;
};

const EMPTY_INITIAL_VALUES: Record<string, unknown> = {};

export const AccordionShell = memo((props: AccordionShellProps) => {
  const {
    title,
    document,
    companyRole,
    comments,
    addComment,
    initialValues,
    canEditPayload = false,
    requiresPayloadSave = false,
    onSavePayload,
    payloadSaveConfirmation,
    onSaveSuccess,
    defaultExpanded,
    children,
  } = props;

  const declineReasons = document?.document.declineReason ?? [];
  const warnings = document?.document.warnings ?? [];
  const hasDeclines = declineReasons.length > 0;
  const hasWarnings = warnings.length > 0;

  // Outer form submit is a no-op; the SaveBar invokes onSavePayload itself so
  // it can sequence the payload save with the comment save and run them under
  // a single loading/error state.
  const handleFormSubmit = useCallback(() => undefined, []);

  return (
    <Accordion
      sx={{ backgroundColor: hasDeclines ? "rgba(0, 0, 0, 0.04)" : "" }}
      disableGutters
      defaultExpanded={defaultExpanded}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${title}-content`}
        id={`${title}-header`}
      >
        <Grid container alignItems="center" spacing={3}>
          <Grid item>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "800",
                fontSize: "20px",
                lineHeight: "28px",
                letterSpacing: "0.5px",
                color: "#1C1F21",
                textTransform: "capitalize",
              }}
            >
              {title}
              {hasWarnings && (
                <WarningRoundedIcon
                  sx={{
                    color: mainTheme.palette.warning.main,
                    verticalAlign: "sub",
                  }}
                />
              )}
            </Typography>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails sx={{ marginTop: "8px" }}>
        {hasDeclines && (
          <TextField
            sx={{
              marginBottom: 4,
              "& .MuiInputBase-root": {
                color: mainTheme.palette.error.main,
              },
            }}
            fullWidth
            value={declineReasons.join(", ")}
            name="declineReason"
            label="Decline reason(s)"
            multiline
            rows={Math.min(
              4,
              Math.ceil(declineReasons.join(", ").length / 100),
            )}
            inputProps={{ disabled: true }}
          />
        )}
        {hasWarnings && (
          <TextField
            sx={{
              marginBottom: 4,
              "& .MuiInputBase-root": {
                color: mainTheme.palette.warning.main,
              },
            }}
            fullWidth
            value={warnings.join(", ")}
            name="warnings"
            label="Warning(s)"
            multiline
            rows={Math.min(4, Math.ceil(warnings.join(", ").length / 100))}
            inputProps={{ disabled: true }}
          />
        )}

        <Form
          onSubmit={handleFormSubmit}
          initialValues={initialValues ?? EMPTY_INITIAL_VALUES}
          subscription={{ submitting: true }}
          render={() => (
            <>
              {children ??
                (document === undefined && (
                  <Typography variant="body2" color="text.secondary">
                    No data submitted
                  </Typography>
                ))}

              {document && (
                <SaveBar
                  documentId={document.document.documentId}
                  initialComment={comments[document.document.documentId] ?? ""}
                  companyRole={companyRole}
                  addComment={addComment}
                  canEditPayload={canEditPayload}
                  requiresPayloadSave={requiresPayloadSave}
                  onSavePayload={onSavePayload}
                  payloadSaveConfirmation={payloadSaveConfirmation}
                  onSaveSuccess={onSaveSuccess}
                />
              )}
            </>
          )}
        />
      </AccordionDetails>
    </Accordion>
  );
});
